import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Platform } from '@ionic/angular';
import { filter, firstValueFrom, map } from 'rxjs';
import { AnalyticsEvents } from '../enums/analytics-events.enum';
import { AuthService } from '@auth0/auth0-angular';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly authService = inject(AuthService);

  authenticated$ = this.authService.idTokenClaims$.pipe(map(Boolean));

  constructor(private activatedRoute: ActivatedRoute, private router: Router, private platform: Platform) {
    this.trackNavigationEvents();
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  async logEvent(name: AnalyticsEvents, params: any = {}): Promise<void> {
    if (!this.platform.is('capacitor')) {
      return;
    }

    const isAuthenticated = await firstValueFrom(this.authenticated$);
    params.user_status = isAuthenticated ? 'authenticated' : 'unauthenticated';

    FirebaseAnalytics.logEvent({ name, params });
  }

  trackNavigationEvents() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const pageTitle = this.getLastChildRouteTitle(this.activatedRoute.root);

      if (!pageTitle) {
        return;
      }

      this.logEvent(AnalyticsEvents.PageView, { screen_name: pageTitle });
    });
  }

  private getLastChildRouteTitle(route: ActivatedRoute, breadcrumbs = []) {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs.pop();
    }

    for (const child of children) {
      const pageTitle = child.snapshot.data['pageTitle'];
      breadcrumbs.push(pageTitle);
      return this.getLastChildRouteTitle(child, breadcrumbs);
    }
  }
}
